import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Table } from 'reactstrap';
import moment from 'moment/moment';
import { axiosGet } from '../../util/apiConfig';
import { accountLabel } from '../../util/constants';

const reasonText = (item) => {
  switch (item.reason) {
    case 'PAYING_IN_OTHER_ACCOUNT': return `Paying in the ${accountLabel(item.otherAccount)} Stripe account, not ${accountLabel(item.account)}`;
    case 'NO_RECENT_PAYMENT': return `Last payment ${item.lastPaidAt ? moment.unix(item.lastPaidAt).format('YYYY-MM-DD') : 'unknown'}; none in the last 45 days`;
    case 'NO_ACTIVE_PAYMENT': return 'No active subscription or payment in the last 6 months';
    case 'NO_STRIPE_CUSTOMER': return 'No Stripe customer for these emails';
    case 'NO_EMAIL': return 'No parent email on the section, so payments cannot be matched';
    case 'NOT_CHECKED': return 'Not checked: a Stripe account failed to load';
    case 'UNKNOWN_CATEGORY': return 'Class has no known payment category';
    default: return item.reason;
  }
};

// Running private sections with no live subscription or recent payment in their own Stripe account.
export default function UnmatchedPayments() {
  const [attempt, setAttempt] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setReport(null);
    axiosGet('payment/unmatched-sections')
      .then((response) => {
        if (!active) return;
        if (response.isError || !response.data || !Array.isArray(response.data.items)) setError(true);
        else setReport(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [attempt]);

  return (
    <Card className="shadow border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
      <CardHeader className="border-0 bg-transparent py-4">
        <h3 className="mb-0" style={{ fontWeight: '700', color: '#32325d' }}>
          <i className="fa fa-question-circle mr-2 text-warning" style={{ fontSize: '1.2rem' }}></i> Un-Matched Payments
        </h3>
        <p className="text-muted small mb-0 mt-2">
          Running private classes with no live subscription or payment in the last 45 days in their own Stripe account. Checks the parent email and the additional email field.
          {report && ` As of ${moment(report.generatedAt).format('YYYY-MM-DD HH:mm')}.`}
        </p>
      </CardHeader>
      <CardBody aria-busy={loading}>
        {loading && <p role="status">Loading un-matched payments…</p>}
        {error && <div role="alert">
          <p>Could not load un-matched payments.</p>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
        </div>}
        {report && <>
          {report.errors.length > 0 && <div role="status" className="alert alert-warning small">
            Could not load {report.errors.map((e) => accountLabel(e.account)).join(', ')} from Stripe
            {report.notChecked > 0 ? `; ${report.notChecked} class(es) in that category were not checked` : ''}. <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
          </div>}
          {report.items.length === 0 ? <p>{report.errors.length ? 'No issues found in the accounts that loaded.' : 'Every running private class has an active payment.'}</p> : (
            <div style={{ maxHeight: '500px', overflow: 'auto' }}>
              <Table className="align-items-center table-flush" responsive size="sm">
                <thead className="thead-light"><tr>
                  <th scope="col">Email</th>
                  <th scope="col">Section Name</th>
                  <th scope="col">Category</th>
                  <th scope="col">Reason</th>
                </tr></thead>
                <tbody>{report.items.map((item) => <tr key={item.sectionId}>
                  <td>{item.emails.map((email) => <div key={email}>{email}</div>)}</td>
                  <td style={{ whiteSpace: 'normal', minWidth: '160px' }}>{item.name}</td>
                  <td>{accountLabel(item.account) || '—'}</td>
                  <td style={{ whiteSpace: 'normal', minWidth: '180px' }}>{reasonText(item)}</td>
                </tr>)}</tbody>
              </Table>
            </div>
          )}
        </>}
      </CardBody>
    </Card>
  );
}
